import { useState, useEffect, useCallback, useRef } from 'react';
import { useThumbnailCache } from '../contexts/ThumbnailCacheContext';
import DefaultThumbnail from '../assets/recording/WhiteThumbnail.svg';

interface UseCachedThumbnailProps {
  fileId: string;
  thumbnailUrl: string | null | undefined;
  fallbackUrl?: string;
}

interface UseCachedThumbnailReturn {
  thumbnailSrc: string;
  isLoading: boolean;
  hasError: boolean;
  handleLoad: () => void;
  handleError: () => void;
}

export const useCachedThumbnail = ({
  fileId,
  thumbnailUrl,
  fallbackUrl = DefaultThumbnail,
}: UseCachedThumbnailProps): UseCachedThumbnailReturn => {
  const { getThumbnail, setThumbnail, preloadThumbnail } = useThumbnailCache();

  // Initialize currentUrlRef before state to track initial URL
  const currentUrlRef = useRef(thumbnailUrl);

  const [localState, setLocalState] = useState(() => {
    const cached = getThumbnail(fileId);
    // Always prefer cached thumbnail if available
    if (cached?.loaded) {
      return {
        src: cached.error ? fallbackUrl : cached.url,
        isLoading: false,
        hasError: cached.error,
      };
    }
    // If not cached, use the provided thumbnail URL without loading state
    // This prevents the white flash on initial render
    return {
      src: thumbnailUrl || fallbackUrl,
      isLoading: false,
      hasError: false,
    };
  });

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const cached = getThumbnail(fileId);

    // If thumbnail URL changed, update
    if (thumbnailUrl !== currentUrlRef.current) {
      currentUrlRef.current = thumbnailUrl;
      if (thumbnailUrl) {
        // Check if we already have this thumbnail cached and loaded
        if (
          cached &&
          cached.loaded &&
          cached.url === thumbnailUrl &&
          !cached.error
        ) {
          // Don't trigger loading state if already cached
          setLocalState({
            src: cached.url,
            isLoading: false,
            hasError: false,
          });
        } else {
          // Only set loading state if not cached
          setLocalState({
            src: thumbnailUrl,
            isLoading: true,
            hasError: false,
          });
          // Preload the new thumbnail
          preloadThumbnail(fileId, thumbnailUrl);
        }
      }
    } else if (cached && cached.loaded) {
      // Use cached data if available - update only if not already in correct state
      setLocalState((prev) => {
        const newSrc = cached.error ? fallbackUrl : cached.url;
        // Only update if state actually changed to prevent unnecessary re-renders
        if (
          prev.src === newSrc &&
          prev.isLoading === false &&
          prev.hasError === cached.error
        ) {
          return prev;
        }
        return {
          src: newSrc,
          isLoading: false,
          hasError: cached.error,
        };
      });
    } else if (thumbnailUrl && !cached) {
      // Start preloading if not cached (but don't show loading skeleton)
      preloadThumbnail(fileId, thumbnailUrl);
    }
  }, [fileId, thumbnailUrl, fallbackUrl, getThumbnail, preloadThumbnail]);

  const handleLoad = useCallback(() => {
    if (!isMountedRef.current) return;

    setThumbnail(fileId, {
      url: localState.src,
      loaded: true,
      error: false,
    });

    setLocalState((prev) => ({
      ...prev,
      isLoading: false,
      hasError: false,
    }));
  }, [fileId, localState.src, setThumbnail]);

  const handleError = useCallback(() => {
    if (!isMountedRef.current) return;

    const isUsingFallback = localState.src === fallbackUrl;

    if (!isUsingFallback) {
      setThumbnail(fileId, {
        url: thumbnailUrl || '',
        loaded: true,
        error: true,
      });

      setLocalState({
        src: fallbackUrl,
        isLoading: false,
        hasError: true,
      });
    } else {
      // Even fallback failed, but still mark as loaded
      setLocalState((prev) => ({
        ...prev,
        isLoading: false,
        hasError: true,
      }));
    }
  }, [fileId, thumbnailUrl, fallbackUrl, localState.src, setThumbnail]);

  return {
    thumbnailSrc: localState.src,
    isLoading: localState.isLoading,
    hasError: localState.hasError,
    handleLoad,
    handleError,
  };
};
